// Bilingual content for the GuideSwahili page (src/components/GuideSwahili.tsx)
//
// Note: Swahili-language example words/phrases (and other foreign-language
// forms such as Arabic, Greek, or German etymological examples) are kept
// IDENTICAL between the fr and en entries — only the surrounding French
// prose is translated into English.

// A run of inline-formatted text within a paragraph or list item. Mirrors
// the <strong>/<em> inline JSX formatting used throughout the original page.
export interface SwahiliSegment {
  text: string;
  style?: 'em' | 'strong';
}

export interface SwahiliGreetingRow {
  gloss: string;
  sw: string;
  pron: string;
}

export interface SwahiliVocabRow {
  gloss: string;
  sw: string;
}

export interface GuideSwahiliContent {
  pageTitleTag: string;

  breadcrumbHome: string;
  breadcrumbGuides: string;
  breadcrumbCurrent: string;

  h1: string;
  intro: string;

  whatIsTitle: string;
  whatIsP1: SwahiliSegment[];
  whatIsP2: SwahiliSegment[];
  whatIsP3: SwahiliSegment[];

  accessibleTitle: string;
  accessibleIntro: string;
  accessibleList: SwahiliSegment[][];

  nounClassesTitle: string;
  nounClassesP1: SwahiliSegment[];
  nounClassesIntro: string;
  nounClassesList: SwahiliSegment[][];

  greetingsTitle: string;
  greetingsTableHeaders: { gloss: string; sw: string; pron: string };
  greetingsRows: SwahiliGreetingRow[];

  vocabTitle: string;
  vocabTableHeaders: { gloss: string; sw: string };
  vocabRows: SwahiliVocabRow[];

  cultureTitle: string;
  cultureP1: SwahiliSegment[];
  cultureP2: SwahiliSegment[];
  cultureP3: SwahiliSegment[];

  ctaTitle: string;
  ctaText: string;
  ctaButtonStart: string;
  ctaButtonWolof: string;
  ctaButtonBambara: string;

  footerHome: string;
  footerAbout: string;
  footerWolof: string;
  footerBambara: string;
}

export const GUIDE_SWAHILI_CONTENT: Record<'fr' | 'en', GuideSwahiliContent> = {
  fr: {
    pageTitleTag: 'Apprendre le Swahili — Guide complet | JogoLinga',

    breadcrumbHome: 'JogoLinga',
    breadcrumbGuides: 'Guides des langues',
    breadcrumbCurrent: 'Swahili',

    h1: 'Apprendre le Swahili (Kiswahili) — Guide complet pour débutants',
    intro: "Le swahili est la langue africaine avec le plus grand nombre de locuteurs — plus de 200 millions. Langue officielle de l'Union Africaine, c'est la clé pour comprendre l'Afrique de l'Est.",

    whatIsTitle: "Qu'est-ce que le Swahili ?",
    whatIsP1: [
      { text: "Le swahili, ou " },
      { text: 'kiswahili', style: 'em' },
      { text: ' (le préfixe ' },
      { text: 'ki-', style: 'em' },
      { text: " désigne la langue en swahili), est la langue la plus parlée d'Afrique subsaharienne en nombre de locuteurs totaux, avec environ " },
      { text: '200 millions de personnes', style: 'strong' },
      { text: " qui le comprennent ou l'utilisent en Tanzanie, au Kenya, en Ouganda, au Rwanda, au Burundi, en République Démocratique du Congo, au Mozambique, en Zambie et dans les îles de l'océan Indien (Comores, Zanzibar, Mayotte)." },
    ],
    whatIsP2: [
      { text: "En 2004, l'" },
      { text: 'Union Africaine', style: 'strong' },
      { text: " a fait du swahili l'une de ses langues de travail officielles, aux côtés de l'arabe, du français, de l'anglais et du portugais. La Tanzanie et le Kenya en ont fait leur langue nationale et officielle. Cette officialisation reflète le rôle historique du swahili comme " },
      { text: 'lingua franca', style: 'em' },
      { text: " : depuis des siècles, il est la langue du commerce, de la diplomatie et de la culture sur toute la côte est-africaine et dans les terres intérieures." },
    ],
    whatIsP3: [
      { text: 'Linguistiquement, le swahili est une langue ' },
      { text: 'bantoue', style: 'strong' },
      { text: " (famille Niger-Congo) profondément enrichie par des siècles d'échanges avec le monde arabo-persan, indien et portugais. Environ " },
      { text: '35 % du vocabulaire swahili', style: 'strong' },
      { text: " est d'origine arabe : les marchands arabes commerçaient sur la côte est-africaine depuis le VIIIe siècle. Des mots comme " },
      { text: 'kitabu', style: 'em' },
      { text: " (livre, de l'arabe " },
      { text: 'kitāb', style: 'em' },
      { text: '), ' },
      { text: 'daktari', style: 'em' },
      { text: ' (médecin, du grec ' },
      { text: 'doktōr', style: 'em' },
      { text: " via l'arabe) ou " },
      { text: 'shule', style: 'em' },
      { text: " (école, de l'allemand " },
      { text: 'Schule', style: 'em' },
      { text: ') témoignent de cette histoire d\'échanges exceptionnelle.' },
    ],

    accessibleTitle: 'Pourquoi le Swahili est accessible aux francophones',
    accessibleIntro: 'Comparé au wolof ou au bambara, le swahili présente plusieurs avantages pour les apprenants francophones :',
    accessibleList: [
      [
        { text: 'Pas de tons', style: 'strong' },
        { text: " : contrairement au bambara ou au lingala, le swahili standard ne distingue pas les mots par la hauteur musicale." },
      ],
      [
        { text: 'Prononciation régulière', style: 'strong' },
        { text: " : les voyelles se prononcent comme en espagnol ou en latin (a, e, i, o, u), et l'accent tombe presque toujours sur l'avant-dernière syllabe." },
      ],
      [
        { text: 'Nombreux mots reconnaissables', style: 'strong' },
        { text: " : les emprunts à l'arabe, au portugais et à l'anglais (" },
        { text: 'hospitali', style: 'em' },
        { text: ', ' },
        { text: 'benchi', style: 'em' },
        { text: ', ' },
        { text: 'saa', style: 'em' },
        { text: ' = heure, ' },
        { text: 'meza', style: 'em' },
        { text: ' = table) facilitent la mémorisation.' },
      ],
      [
        { text: 'Grammaire agglutinante et régulière', style: 'strong' },
        { text: " : les préfixes s'ajoutent à des racines stables, ce qui rend la conjugaison systématique une fois les préfixes appris." },
      ],
    ],

    nounClassesTitle: 'Grammaire Swahili — Les classes nominales',
    nounClassesP1: [
      { text: 'La grande complexité du swahili réside dans ses ' },
      { text: 'classes nominales', style: 'strong' },
      { text: " : chaque nom appartient à une classe, définie par son préfixe. Ces classes gouvernent les accords entre le nom, l'adjectif et le verbe — un système comparable au genre grammatical français, mais avec 8 à 15 classes au lieu de deux." },
    ],
    nounClassesIntro: 'Les classes principales pour les débutants :',
    nounClassesList: [
      [
        { text: 'Classe m-/wa-', style: 'strong' },
        { text: ' : personnes humaines. ' },
        { text: 'Mtoto', style: 'em' },
        { text: ' (enfant) → ' },
        { text: 'Watoto', style: 'em' },
        { text: ' (enfants)' },
      ],
      [
        { text: 'Classe m-/mi-', style: 'strong' },
        { text: ' : arbres, plantes, objets allongés. ' },
        { text: 'Mti', style: 'em' },
        { text: ' (arbre) → ' },
        { text: 'Miti', style: 'em' },
        { text: ' (arbres)' },
      ],
      [
        { text: 'Classe ki-/vi-', style: 'strong' },
        { text: ' : objets fabriqués, langues. ' },
        { text: 'Kitu', style: 'em' },
        { text: ' (chose) → ' },
        { text: 'Vitu', style: 'em' },
        { text: ' (choses) ; ' },
        { text: 'Kiswahili', style: 'em' },
        { text: ' (la langue swahili)' },
      ],
      [
        { text: 'Classe n-/n-', style: 'strong' },
        { text: ' : animaux, mots empruntés. ' },
        { text: 'Ndege', style: 'em' },
        { text: ' (oiseau/avion) → ' },
        { text: 'Ndege', style: 'em' },
        { text: ' (invariable)' },
      ],
    ],

    greetingsTitle: 'Salutations et expressions indispensables',
    greetingsTableHeaders: { gloss: 'Français', sw: 'Swahili', pron: 'Prononciation' },
    greetingsRows: [
      { gloss: 'Bonjour (informel)', sw: 'Habari ?', pron: 'ha-BA-ri ?' },
      { gloss: 'Bonjour (formel, à un aîné)', sw: 'Shikamoo', pron: 'chi-KA-moo' },
      { gloss: 'Réponse formelle', sw: 'Marahaba', pron: 'ma-ra-HA-ba' },
      { gloss: 'Comment vas-tu ?', sw: 'U hali gani ?', pron: 'u HA-li GA-ni ?' },
      { gloss: 'Je vais bien', sw: 'Nzuri / Sijambo', pron: 'NZU-ri / si-JAM-bo' },
      { gloss: 'Merci', sw: 'Asante', pron: 'a-SAN-te' },
      { gloss: 'Merci beaucoup', sw: 'Asante sana', pron: 'a-SAN-te SA-na' },
      { gloss: "S'il vous plaît", sw: 'Tafadhali', pron: 'ta-fa-DHA-li' },
      { gloss: 'Excuse-moi', sw: 'Samahani', pron: 'sa-ma-HA-ni' },
      { gloss: 'Oui', sw: 'Ndiyo', pron: 'NDI-yo' },
      { gloss: 'Non', sw: 'Hapana', pron: 'ha-PA-na' },
      { gloss: 'Au revoir', sw: 'Kwaheri', pron: 'kwa-HE-ri' },
    ],

    vocabTitle: 'Vocabulaire Swahili — Voyage et orientation',
    vocabTableHeaders: { gloss: 'Français', sw: 'Swahili' },
    vocabRows: [
      { gloss: 'Où est… ?', sw: 'Iko wapi… ?' },
      { gloss: 'Hôtel', sw: 'Hoteli' },
      { gloss: 'Hôpital', sw: 'Hospitali' },
      { gloss: 'Gare / Arrêt de bus', sw: 'Stendi' },
      { gloss: 'Aéroport', sw: 'Uwanja wa ndege' },
      { gloss: 'Marché', sw: 'Soko' },
      { gloss: 'Route', sw: 'Barabara' },
      { gloss: 'À gauche', sw: 'Kushoto' },
      { gloss: 'À droite', sw: 'Kulia' },
      { gloss: 'Tout droit', sw: 'Moja kwa moja' },
      { gloss: 'Loin', sw: 'Mbali' },
      { gloss: 'Proche', sw: 'Karibu' },
    ],

    cultureTitle: 'Culture swahili — Zanzibar, la côte et Ubuntu',
    cultureP1: [
      { text: 'La ' },
      { text: 'civilisation swahili', style: 'strong' },
      { text: " est l'une des grandes civilisations maritimes de l'Afrique. De la cité-État de Kilwa (actuelle Tanzanie) au port de Mombasa (Kenya), des douzaines de sultanats swahilis contrôlaient le commerce de l'or, de l'ivoire et des épices entre le XIe et le XIXe siècle. L'île de " },
      { text: 'Zanzibar', style: 'strong' },
      { text: ', avec ses ruelles de Stone Town et son palais de Beit el-Ajaib ("Maison des Merveilles"), est le cœur de cette civilisation encore vivante.' },
    ],
    cultureP2: [
      { text: 'La philosophie ' },
      { text: 'Ubuntu', style: 'strong' },
      { text: ' (' },
      { text: 'Mtu ni watu', style: 'em' },
      { text: ' en swahili — "une personne est une personne à travers les autres") est profondément ancrée dans les cultures est-africaines. Ce concept de solidarité communautaire, popularisé par Nelson Mandela et Desmond Tutu, exprime une vision du monde où l\'identité individuelle se construit dans la relation aux autres.' },
    ],
    cultureP3: [
      { text: 'La musique ' },
      { text: 'bongo flava', style: 'strong' },
      { text: ' (hip-hop tanzanien), le ' },
      { text: 'taarab', style: 'strong' },
      { text: ' (musique de Zanzibar mêlant influences arabes, indiennes et africaines) et les artistes comme Diamond Platnumz ont exporté le swahili dans le monde entier. Freddie Mercury, le légendaire chanteur de Queen, était lui-même d\'origine zanzibarite et s\'appelait Farrokh Bulsara avant de devenir célèbre.' },
    ],

    ctaTitle: 'Apprendre le Swahili avec JogoLinga',
    ctaText: 'JogoLinga propose des leçons de swahili avec plus de 500 mots organisés par thèmes (voyage, famille, nourriture, corps, couleurs), des exercices sur les classes nominales, des dialogues interactifs et un suivi de progression quotidien. Gratuit et sans inscription obligatoire.',
    ctaButtonStart: 'Commencer à apprendre le Swahili →',
    ctaButtonWolof: 'Guide Wolof',
    ctaButtonBambara: 'Guide Bambara',

    footerHome: 'Accueil',
    footerAbout: 'À propos',
    footerWolof: 'Guide Wolof',
    footerBambara: 'Guide Bambara',
  },

  en: {
    pageTitleTag: 'Learn Swahili — Complete Guide | JogoLinga',

    breadcrumbHome: 'JogoLinga',
    breadcrumbGuides: 'Language guides',
    breadcrumbCurrent: 'Swahili',

    h1: 'Learn Swahili (Kiswahili) — A Complete Guide for Beginners',
    intro: 'Swahili is the African language with the largest number of speakers — over 200 million. As an official language of the African Union, it is the key to understanding East Africa.',

    whatIsTitle: 'What is Swahili?',
    whatIsP1: [
      { text: 'Swahili, or ' },
      { text: 'kiswahili', style: 'em' },
      { text: ' (the prefix ' },
      { text: 'ki-', style: 'em' },
      { text: ' denotes a language in Swahili), is the most widely spoken language in Sub-Saharan Africa by total number of speakers, with around ' },
      { text: '200 million people', style: 'strong' },
      { text: ' who understand or use it in Tanzania, Kenya, Uganda, Rwanda, Burundi, the Democratic Republic of the Congo, Mozambique, Zambia, and the islands of the Indian Ocean (Comoros, Zanzibar, Mayotte).' },
    ],
    whatIsP2: [
      { text: 'In 2004, the ' },
      { text: 'African Union', style: 'strong' },
      { text: ' made Swahili one of its official working languages, alongside Arabic, French, English, and Portuguese. Tanzania and Kenya have made it their national and official language. This official status reflects Swahili\'s historic role as a ' },
      { text: 'lingua franca', style: 'em' },
      { text: ': for centuries, it has been the language of trade, diplomacy, and culture across the entire East African coast and the interior.' },
    ],
    whatIsP3: [
      { text: 'Linguistically, Swahili is a ' },
      { text: 'Bantu', style: 'strong' },
      { text: ' language (Niger-Congo family) deeply enriched by centuries of exchange with the Arab-Persian, Indian, and Portuguese worlds. Around ' },
      { text: '35% of Swahili vocabulary', style: 'strong' },
      { text: ' is of Arabic origin: Arab merchants traded along the East African coast from the 8th century onward. Words like ' },
      { text: 'kitabu', style: 'em' },
      { text: ' (book, from the Arabic ' },
      { text: 'kitāb', style: 'em' },
      { text: '), ' },
      { text: 'daktari', style: 'em' },
      { text: ' (doctor, from the Greek ' },
      { text: 'doktōr', style: 'em' },
      { text: ' via Arabic), or ' },
      { text: 'shule', style: 'em' },
      { text: ' (school, from the German ' },
      { text: 'Schule', style: 'em' },
      { text: ') bear witness to this exceptional history of exchange.' },
    ],

    accessibleTitle: 'Why Swahili is accessible to French speakers',
    accessibleIntro: 'Compared to Wolof or Bambara, Swahili offers several advantages for French-speaking learners:',
    accessibleList: [
      [
        { text: 'No tones', style: 'strong' },
        { text: ': unlike Bambara or Lingala, standard Swahili does not distinguish words by musical pitch.' },
      ],
      [
        { text: 'Regular pronunciation', style: 'strong' },
        { text: ': vowels are pronounced as in Spanish or Latin (a, e, i, o, u), and the stress almost always falls on the second-to-last syllable.' },
      ],
      [
        { text: 'Many recognizable words', style: 'strong' },
        { text: ': loanwords from Arabic, Portuguese, and English (' },
        { text: 'hospitali', style: 'em' },
        { text: ', ' },
        { text: 'benchi', style: 'em' },
        { text: ', ' },
        { text: 'saa', style: 'em' },
        { text: ' = hour, ' },
        { text: 'meza', style: 'em' },
        { text: ' = table) make them easier to remember.' },
      ],
      [
        { text: 'Regular, agglutinative grammar', style: 'strong' },
        { text: ': prefixes attach to stable roots, which makes conjugation systematic once the prefixes are learned.' },
      ],
    ],

    nounClassesTitle: 'Swahili Grammar — Noun Classes',
    nounClassesP1: [
      { text: 'The great complexity of Swahili lies in its ' },
      { text: 'noun classes', style: 'strong' },
      { text: ': every noun belongs to a class, defined by its prefix. These classes govern agreement between the noun, the adjective, and the verb — a system comparable to grammatical gender in French, but with 8 to 15 classes instead of two.' },
    ],
    nounClassesIntro: 'The main classes for beginners:',
    nounClassesList: [
      [
        { text: 'Class m-/wa-', style: 'strong' },
        { text: ': human beings. ' },
        { text: 'Mtoto', style: 'em' },
        { text: ' (child) → ' },
        { text: 'Watoto', style: 'em' },
        { text: ' (children)' },
      ],
      [
        { text: 'Class m-/mi-', style: 'strong' },
        { text: ': trees, plants, elongated objects. ' },
        { text: 'Mti', style: 'em' },
        { text: ' (tree) → ' },
        { text: 'Miti', style: 'em' },
        { text: ' (trees)' },
      ],
      [
        { text: 'Class ki-/vi-', style: 'strong' },
        { text: ': manufactured objects, languages. ' },
        { text: 'Kitu', style: 'em' },
        { text: ' (thing) → ' },
        { text: 'Vitu', style: 'em' },
        { text: ' (things); ' },
        { text: 'Kiswahili', style: 'em' },
        { text: ' (the Swahili language)' },
      ],
      [
        { text: 'Class n-/n-', style: 'strong' },
        { text: ': animals, borrowed words. ' },
        { text: 'Ndege', style: 'em' },
        { text: ' (bird/airplane) → ' },
        { text: 'Ndege', style: 'em' },
        { text: ' (invariable)' },
      ],
    ],

    greetingsTitle: 'Essential Greetings and Expressions',
    greetingsTableHeaders: { gloss: 'English', sw: 'Swahili', pron: 'Pronunciation' },
    greetingsRows: [
      { gloss: 'Hello (informal)', sw: 'Habari ?', pron: 'ha-BA-ri ?' },
      { gloss: 'Hello (formal, to an elder)', sw: 'Shikamoo', pron: 'chi-KA-moo' },
      { gloss: 'Formal reply', sw: 'Marahaba', pron: 'ma-ra-HA-ba' },
      { gloss: 'How are you?', sw: 'U hali gani ?', pron: 'u HA-li GA-ni ?' },
      { gloss: 'I am fine', sw: 'Nzuri / Sijambo', pron: 'NZU-ri / si-JAM-bo' },
      { gloss: 'Thank you', sw: 'Asante', pron: 'a-SAN-te' },
      { gloss: 'Thank you very much', sw: 'Asante sana', pron: 'a-SAN-te SA-na' },
      { gloss: 'Please', sw: 'Tafadhali', pron: 'ta-fa-DHA-li' },
      { gloss: 'Excuse me', sw: 'Samahani', pron: 'sa-ma-HA-ni' },
      { gloss: 'Yes', sw: 'Ndiyo', pron: 'NDI-yo' },
      { gloss: 'No', sw: 'Hapana', pron: 'ha-PA-na' },
      { gloss: 'Goodbye', sw: 'Kwaheri', pron: 'kwa-HE-ri' },
    ],

    vocabTitle: 'Swahili Vocabulary — Travel and Directions',
    vocabTableHeaders: { gloss: 'English', sw: 'Swahili' },
    vocabRows: [
      { gloss: 'Where is…?', sw: 'Iko wapi… ?' },
      { gloss: 'Hotel', sw: 'Hoteli' },
      { gloss: 'Hospital', sw: 'Hospitali' },
      { gloss: 'Train station / Bus stop', sw: 'Stendi' },
      { gloss: 'Airport', sw: 'Uwanja wa ndege' },
      { gloss: 'Market', sw: 'Soko' },
      { gloss: 'Road', sw: 'Barabara' },
      { gloss: 'Left', sw: 'Kushoto' },
      { gloss: 'Right', sw: 'Kulia' },
      { gloss: 'Straight ahead', sw: 'Moja kwa moja' },
      { gloss: 'Far', sw: 'Mbali' },
      { gloss: 'Near', sw: 'Karibu' },
    ],

    cultureTitle: 'Swahili Culture — Zanzibar, the Coast, and Ubuntu',
    cultureP1: [
      { text: 'The ' },
      { text: 'Swahili civilization', style: 'strong' },
      { text: ' is one of Africa\'s great maritime civilizations. From the city-state of Kilwa (present-day Tanzania) to the port of Mombasa (Kenya), dozens of Swahili sultanates controlled the trade in gold, ivory, and spices between the 11th and 19th centuries. The island of ' },
      { text: 'Zanzibar', style: 'strong' },
      { text: ', with the narrow lanes of Stone Town and its Beit el-Ajaib palace ("House of Wonders"), is the heart of this still-living civilization.' },
    ],
    cultureP2: [
      { text: 'The ' },
      { text: 'Ubuntu', style: 'strong' },
      { text: ' philosophy (' },
      { text: 'Mtu ni watu', style: 'em' },
      { text: ' in Swahili — "a person is a person through other people") is deeply rooted in East African cultures. This concept of communal solidarity, popularized by Nelson Mandela and Desmond Tutu, expresses a worldview in which individual identity is built through relationships with others.' },
    ],
    cultureP3: [
      { text: 'The music genres ' },
      { text: 'bongo flava', style: 'strong' },
      { text: ' (Tanzanian hip-hop) and ' },
      { text: 'taarab', style: 'strong' },
      { text: ' (Zanzibari music blending Arab, Indian, and African influences), along with artists like Diamond Platnumz, have carried Swahili around the world. Freddie Mercury, the legendary singer of Queen, was himself of Zanzibari origin and was named Farrokh Bulsara before he became famous.' },
    ],

    ctaTitle: 'Learn Swahili with JogoLinga',
    ctaText: 'JogoLinga offers Swahili lessons with more than 500 words organized by theme (travel, family, food, the body, colors), exercises on noun classes, interactive dialogues, and daily progress tracking. Free, with no registration required.',
    ctaButtonStart: 'Start Learning Swahili →',
    ctaButtonWolof: 'Wolof Guide',
    ctaButtonBambara: 'Bambara Guide',

    footerHome: 'Home',
    footerAbout: 'About',
    footerWolof: 'Wolof Guide',
    footerBambara: 'Bambara Guide',
  },
};
